import type { Clave } from './i18n';

/**
 * Las perillas de estilo visual: qué valores existen y con qué claves se nombran.
 *
 * ── Por qué acá van CLAVES y no texto ─────────────────────────────────────────
 * Antes tenía las etiquetas escritas en castellano. Se escribió cuando el panel era sólo en
 * castellano y quedó fuera del diccionario al agregarse el inglés: el resto de la pantalla
 * migró y esto no, así que en un panel en inglés el bloque de perfil salía en castellano.
 * Eran las 14 últimas cadenas del panel fuera del diccionario.
 *
 * Con claves, el compilador obliga a que cada una exista en los dos idiomas —`Clave` es
 * `keyof typeof ES` y el diccionario inglés es un `Record<Clave, string>`— así que agregar una
 * opción sin traducirla no compila. Es la misma garantía que ya tenía el resto del panel.
 *
 * ── Qué NO vive acá ───────────────────────────────────────────────────────────
 * Los valores canónicos y los fragmentos de prompt que producen están en
 * `packages/domain/src/creative-profile.ts`. Esto es sólo cómo se llaman en pantalla.
 */

export type ProfileOption = { value: string; label: Clave; detail: Clave };

export const ESTILO_ETARIO_COPY: readonly ProfileOption[] = [
  { value: 'veinte', label: 'perfil.estilo.veinte', detail: 'perfil.estilo.veinte.det' },
  { value: 'producida', label: 'perfil.estilo.producida', detail: 'perfil.estilo.producida.det' },
];

export const REGISTER_COPY: readonly ProfileOption[] = [
  { value: 'natural', label: 'perfil.reg.natural', detail: 'perfil.reg.natural.det' },
  { value: 'editorial', label: 'perfil.reg.editorial', detail: 'perfil.reg.editorial.det' },
  { value: 'amateur', label: 'perfil.reg.amateur', detail: 'perfil.reg.amateur.det' },
];

export const SKIN_LEVEL_COPY: readonly ProfileOption[] = [
  { value: 'calle', label: 'perfil.piel.calle', detail: 'perfil.piel.calle.det' },
  { value: 'deportivo', label: 'perfil.piel.deportivo', detail: 'perfil.piel.deportivo.det' },
  { value: 'playa', label: 'perfil.piel.playa', detail: 'perfil.piel.playa.det' },
  { value: 'sensual', label: 'perfil.piel.sensual', detail: 'perfil.piel.sensual.det' },
  { value: 'atrevido', label: 'perfil.piel.atrevido', detail: 'perfil.piel.atrevido.det' },
];

export const BODY_TYPE_COPY: readonly ProfileOption[] = [
  { value: 'sin_preferencia', label: 'perfil.cuerpo.sin', detail: 'perfil.cuerpo.sin.det' },
  { value: 'atletico', label: 'perfil.cuerpo.atletico', detail: 'perfil.cuerpo.atletico.det' },
  { value: 'curvilineo', label: 'perfil.cuerpo.curvilineo', detail: 'perfil.cuerpo.curvilineo.det' },
  { value: 'delgado', label: 'perfil.cuerpo.delgado', detail: 'perfil.cuerpo.delgado.det' },
];

export const FEED_TONE_COPY: readonly ProfileOption[] = [
  { value: 'neutro', label: 'perfil.tono.neutro', detail: 'perfil.tono.neutro.det' },
  { value: 'calido', label: 'perfil.tono.calido', detail: 'perfil.tono.calido.det' },
  { value: 'frio', label: 'perfil.tono.frio', detail: 'perfil.tono.frio.det' },
  { value: 'contraste', label: 'perfil.tono.contraste', detail: 'perfil.tono.contraste.det' },
];

export const SCENE_SETTING_COPY: readonly ProfileOption[] = [
  { value: 'mezcla', label: 'perfil.escena.mezcla', detail: 'perfil.escena.mezcla.det' },
  { value: 'interiores', label: 'perfil.escena.interiores', detail: 'perfil.escena.interiores.det' },
  { value: 'exteriores', label: 'perfil.escena.exteriores', detail: 'perfil.escena.exteriores.det' },
];

/** Ordenadas de más casera a más producida, que es como se elige. */
export const ILUMINACION_COPY: readonly ProfileOption[] = [
  { value: 'frontal', label: 'perfil.luz.frontal', detail: 'perfil.luz.frontal.det' },
  { value: 'flash', label: 'perfil.luz.flash', detail: 'perfil.luz.flash.det' },
  { value: 'interior_noche', label: 'perfil.luz.noche', detail: 'perfil.luz.noche.det' },
  { value: 'ventana', label: 'perfil.luz.ventana', detail: 'perfil.luz.ventana.det' },
  { value: 'contraluz', label: 'perfil.luz.contraluz', detail: 'perfil.luz.contraluz.det' },
  { value: 'tarde', label: 'perfil.luz.tarde', detail: 'perfil.luz.tarde.det' },
  { value: 'neon', label: 'perfil.luz.neon', detail: 'perfil.luz.neon.det' },
  { value: 'estudio', label: 'perfil.luz.estudio', detail: 'perfil.luz.estudio.det' },
];

/**
 * Los niveles que un proveedor con filtro rechaza, y por eso eligen proveedor.
 *
 * Se conserva acá porque es una regla de producto que la pantalla necesita para avisar antes de
 * gastar, no una etiqueta.
 */
export function necesitaProveedorSinFiltro(skinLevel: string): boolean {
  return skinLevel === 'sensual' || skinLevel === 'atrevido';
}

/** Se combina con cualquiera de las ocho luces: la misma cámara mala de día y de noche. */
export const CALIDAD_COPY: readonly ProfileOption[] = [
  { value: 'limpia', label: 'perfil.calidad.limpia', detail: 'perfil.calidad.limpia.det' },
  { value: 'cruda', label: 'perfil.calidad.cruda', detail: 'perfil.calidad.cruda.det' },
];
